import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import TagSelector from '../components/TagSelector';
import EquipmentSelector from '../components/EquipmentSelector';
import ColorSelector from '../components/ColorSelector';
import './Fleet.css';

function EditFleet({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: '',
    firstRegistration: '',
    licensePlate: '',
    vin: '',
    mileage: 0,
    purchasePrice: 0,
    marketValue: 0,
    purchaseDate: new Date().toISOString().split('T')[0],
    description: '',
    image: null,
    tags: [],
    color: '',
    fuelType: '',
    transmission: '',
    engineCode: ''
  });

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId, id]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadTags(),
        id ? loadVehicle() : Promise.resolve()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };

  const loadVehicle = async () => {
    try {
      const data = await api.get(`/cars/${id}`, tenantId);
      setFormData({
        make: data.make || '',
        model: data.model || '',
        year: data.year || '',
        firstRegistration: data.firstRegistration ? (data.firstRegistration.includes('T') ? data.firstRegistration.split('T')[0] : data.firstRegistration) : '',
        licensePlate: data.licensePlate || '',
        vin: data.vin || '',
        mileage: data.mileage || 0,
        purchasePrice: data.purchasePrice || 0,
        marketValue: data.marketValue || 0,
        purchaseDate: data.purchaseDate ? (data.purchaseDate.includes('T') ? data.purchaseDate.split('T')[0] : data.purchaseDate) : new Date().toISOString().split('T')[0],
        description: data.description || '',
        image: data.image || null,
        tags: data.tags || [],
        color: data.color || '',
        fuelType: data.fuelType || '',
        transmission: data.transmission || '',
        engineCode: data.engineCode || ''
      });
      setSelectedTags(data.tags || []);
      setSelectedEquipment(data.equipment || []);
    } catch (error) {
      console.error('Error loading vehicle:', error);
      showNotification('Fehler beim Laden des Fahrzeugs', 'error');
      navigate('/fleet');
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        tags: selectedTags,
        equipment: selectedEquipment
      };
      if (id) {
        await api.put(`/cars/${id}`, submitData, tenantId);
        showNotification('Fahrzeug erfolgreich aktualisiert', 'success');
      } else {
        await api.post('/cars', submitData, tenantId);
        showNotification('Fahrzeug erfolgreich hinzugefügt', 'success');
      }
      navigate('/fleet');
    } catch (error) {
      console.error('Error saving vehicle:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Fehler beim Speichern des Fahrzeugs';
      showNotification(errorMessage, 'error');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      showNotification('Bitte wählen Sie eine Bilddatei aus', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification('Die Datei ist zu groß. Maximal 5MB erlaubt.', 'error');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxWidth = 1200;
        const maxHeight = 1200;
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        let base64String = canvas.toDataURL('image/jpeg', 0.7);
        let quality = 0.7;
        while (base64String.length > 1.5 * 1024 * 1024 && quality > 0.3) {
          quality -= 0.1;
          base64String = canvas.toDataURL('image/jpeg', quality);
        }
        if (base64String.length > 2 * 1024 * 1024) {
          showNotification('Bild ist zu groß auch nach Kompression. Bitte wählen Sie ein kleineres Bild.', 'error');
          return;
        }
        setFormData({ ...formData, image: base64String });
      };
      img.onerror = () => {
        showNotification('Fehler beim Laden des Bildes', 'error');
      };
      img.src = reader.result;
    };
    reader.onerror = () => {
      showNotification('Fehler beim Lesen der Datei', 'error');
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="fleet-edit">
      <div className="page-header">
        <button type="button" onClick={() => navigate('/fleet')} className="btn-secondary">
          ← Zurück
        </button>
        <h1>{id ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}</h1>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Fahrzeugdaten</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Marke *</label>
                <input
                  type="text"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Modell *</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Baujahr</label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value === '' ? '' : e.target.value })}
                  onBlur={(e) => {
                    const val = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                    setFormData({ ...formData, year: val });
                  }}
                  min="1900"
                  max={new Date().getFullYear() + 1}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Erstzulassung</label>
                <input
                  type="date"
                  value={formData.firstRegistration}
                  onChange={(e) => setFormData({ ...formData, firstRegistration: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Kennzeichen</label>
                <input
                  type="text"
                  value={formData.licensePlate}
                  onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="form-group">
                <label>FIN/VIN</label>
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kilometerstand</label>
                <input
                  type="number"
                  value={formData.mileage}
                  onChange={(e) => setFormData({ ...formData, mileage: e.target.value === '' ? '' : e.target.value })}
                  onBlur={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    setFormData({ ...formData, mileage: val });
                  }}
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kraftstoffart</label>
                <select
                  value={formData.fuelType || ''}
                  onChange={(e) => setFormData({ ...formData, fuelType: e.target.value })}
                >
                  <option value="">Bitte wählen</option>
                  <option value="BENZIN">Benzin</option>
                  <option value="DIESEL">Diesel</option>
                  <option value="ELEKTRO">Elektro</option>
                  <option value="HYBRID">Hybrid</option>
                  <option value="LPG">LPG</option>
                  <option value="CNG">CNG/Erdgas</option>
                </select>
              </div>
              <div className="form-group">
                <label>Getriebeart</label>
                <select
                  value={formData.transmission || ''}
                  onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                >
                  <option value="">Bitte wählen</option>
                  <option value="MANUAL">Manuell</option>
                  <option value="AUTOMATIC">Automatik</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <ColorSelector
                value={formData.color || ''}
                onChange={(color) => setFormData({ ...formData, color })}
                label="Farbe"
              />
            </div>

            <div className="form-group">
              <label>Motorkennbuchstabe</label>
              <input
                type="text"
                value={formData.engineCode || ''}
                onChange={(e) => setFormData({ ...formData, engineCode: e.target.value.toUpperCase() })}
                placeholder="z.B. ANA"
                maxLength="4"
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Kaufdaten</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Ankaufspreis ({currency})</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.purchasePrice}
                  onChange={(e) => setFormData({ ...formData, purchasePrice: parseFloat(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Marktwert ({currency})</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.marketValue}
                  onChange={(e) => setFormData({ ...formData, marketValue: parseFloat(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Ankaufsdatum</label>
                <input
                  type="date"
                  value={formData.purchaseDate}
                  onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Weitere Informationen</h2>
            
            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="5"
                placeholder="Zusätzliche Informationen zum Fahrzeug..."
              />
            </div>

            <div className="form-group">
              <label>Tags</label>
              <TagSelector
                tenantId={tenantId}
                selectedTags={selectedTags}
                onChange={setSelectedTags}
              />
            </div>

            <div className="form-group">
              <EquipmentSelector
                tenantId={tenantId}
                selectedEquipment={selectedEquipment}
                onChange={setSelectedEquipment}
                make={formData.make}
              />
            </div>

            <div className="form-group">
              <label>Fahrzeugbild</label>
              {formData.image ? (
                <div style={{ marginBottom: '1rem' }}>
                  <img 
                    src={formData.image} 
                    alt="Fahrzeug" 
                    style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '4px', marginBottom: '0.5rem' }}
                  />
                  <div>
                    <label className="btn-small btn-secondary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                      Bild ändern
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image: null })}
                      className="btn-small btn-secondary"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      Bild entfernen
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="btn-small" style={{ cursor: 'pointer', display: 'inline-block' }}>
                    Bild hochladen
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {id ? 'Speichern' : 'Erstellen'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/fleet')}
              className="btn-secondary"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditFleet;

